import React, { useState, useContext, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../Context/AuthContext";
import {
  getLoansByAdminIdAndRole,
  getAllCompanies,
  getAssignedBanks
} from "../../src/middleware/backend_hepler";
import DateFilterModal from "../components/DateFilterModal";
import { FaEdit } from "react-icons/fa";
import { faCalendarAlt, faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../components/Loader";
import EditHomeViewModal from "../pages/Forms/HomeLoan/EditHomeViewModal";
import EditPersonalViewModal from "../pages/Forms/PersonalLoan/EditPersonalViewModal";
import EditBusinessViewModal from "../pages/Forms/BusinessLoan/EditBusinessViewModal";
import EditCarViewModal from "../pages/Forms/CarLoan/EditCarViewModal";
import EditMortgageViewModal from "../pages/Forms/MortgageLoan/EditMortgageViewModal";
import EditCreditViewModal from "../pages/Forms/CreditCard/EditCreditViewModal";
import EditInsuranceViewModal from "../pages/Forms/Insurance/EditInsuranceViewModal";
import EditDoctorViewModal from "../pages/Forms/DoctorLoan/EditDoctorViewModal";
import EditNriViewModal from "../pages/Forms/NriLoan/EditNriViewModal";
import Paginations from "../components/Paginations";

const DashboardLayout = ({ children }) => {
  const { adminRole, adminName, adminId } = useContext(AuthContext);
  const [allLoans, setAllLoans] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState("dashboard");
  const [messageDisplayed, setMessageDisplayed] = React.useState(false);
  const [isDropdownVisible, setDropdownVisible] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedLoanId, setSelectedLoanId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loansPerPage, setLoansPerPage] = useState(10);
  const [companyLists, setCompanyLists] = useState([]);
  const [bankLists, setBankLists] = useState([]);

  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (location.state?.successMessage && !messageDisplayed) {
      toast.success(location.state.successMessage);
      setMessageDisplayed(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, messageDisplayed]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownVisible(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const loadLoansData = async () => {
      try {
        setLoading(true);
        const [allLoansData] = await Promise.all([
          getLoansByAdminIdAndRole(adminId, adminRole),
        ]);
        setAllLoans(allLoansData);
      } catch (err) {
        setError("Failed to load loans data.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadLoansData();
  }, [adminId, adminRole]);

  useEffect(() => {
    const fetchCompanyListsAndBanks = async () => {
      try {
        setLoading(true);

        // company list API
        const response = await getAllCompanies();
        const companies = response.data;
        setCompanyLists(companies);

        // assigned banks API
        if (companies.length > 0) {
          const allBankIds = [
            ...new Set(
              companies
                .map((c) => {
                  try {
                    const parsed = JSON.parse(c.bank_list);
                    return parsed ?? [];
                  } catch {
                    return [];
                  }
                })
                .flat()
            ),
          ];

          const bankResponse = await getAssignedBanks(allBankIds);
          setBankLists(bankResponse.data);
        }

        setLoading(false);
      } catch (error) {
        console.error(error);
        setError("Error fetching company or bank lists.");
        setLoading(false);
      }
    };

    fetchCompanyListsAndBanks();
  }, []);

  let filteredAllLoans = [];

  if (adminRole === "admin") {
    filteredAllLoans = allLoans;
  } else if (adminRole === "employee") {
    filteredAllLoans = allLoans.filter((loan) => {
      const assignedToId = loan.assigned_to?.split(" - ")[0]?.replace("ID", "");
      const assignedToName = loan.assigned_to?.split(" - ")[1];

      return (
        loan.created_by === adminName ||
        loan.created_id === adminId ||
        assignedToId === adminId ||
        assignedToName === adminName
      );
    });
  } else if (adminRole === "customer") {
    filteredAllLoans = allLoans.filter(
      (loan) => loan.created_id === adminId || loan.created_by === adminName
    );
  }

  filteredAllLoans = filteredAllLoans.filter(
    (loan) =>
      !loan.form_id?.startsWith("CCL") &&
      !loan.form_id?.startsWith("CL") &&
      !loan.form_id?.startsWith("IL")
  );

  const getBankNames = (bankListString, companyName) => {
    try {
      const bankIds = JSON.parse(bankListString);
      const bankNames = bankIds
        .map((bankId) => {
          const bank = bankLists.find((b) => b.bank_id === bankId);
          return bank ? bank.bank_name : null;
        })
        .filter((name) => name);
      return bankNames.length > 0 ? bankNames.join(", ") : companyName; 
    } catch {
      return companyName; 
    }
  };

  const loanCounts = {
    homeLoan: filteredAllLoans.filter((loan) =>
      loan.form_id?.startsWith("HL")
    ).length,
    personalLoan: filteredAllLoans.filter((loan) =>
      loan.form_id?.startsWith("PL")
    ).length,
    businessLoan: filteredAllLoans.filter((loan) =>
      loan.form_id?.startsWith("BL")
    ).length,
    carLoan: filteredAllLoans.filter((loan) => loan.form_id?.startsWith("CL"))
      .length,
    creditCard: filteredAllLoans.filter((loan) =>
      loan.form_id?.startsWith("CCL")
    ).length,
    insurance: filteredAllLoans.filter((loan) =>
      loan.form_id?.startsWith("IL")
    ).length,
    doctorLoan: filteredAllLoans.filter((loan) =>
      loan.form_id?.startsWith("DL")
    ).length,
    mortgageLoan: filteredAllLoans.filter((loan) =>
      loan.form_id?.startsWith("ML")
    ).length,
    nriLoan: filteredAllLoans.filter((loan) =>
      loan.form_id?.startsWith("NRI")
    ).length,
  };

  const isWithinDateRange = (dateString) => {
    if (!startDate && !endDate) return true;

    const loanDate = new Date(dateString);
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;

    if (start) {
      start.setHours(0, 0, 0, 0);
    }

    if (end) {
      end.setHours(23, 59, 59, 999);
    }

    if (start && end && start.toDateString() === end.toDateString()) {
      return loanDate.toDateString() === start.toDateString();
    }

    if (start && end) {
      return loanDate >= start && loanDate <= end;
    }

    if (start) {
      return loanDate >= start;
    }

    if (end) {
      return loanDate <= end;
    }

    return true;
  };

  const filteredLoans = filteredAllLoans
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .filter((loan) => {
      const searchQueryLower = searchQuery.toLowerCase();

      const matchesSearch =
        loan.name?.toLowerCase().includes(searchQueryLower) ||
        loan.form_id?.toString().toLowerCase().includes(searchQueryLower) ||
        loan.mobile?.toString().toLowerCase().includes(searchQueryLower) ||
        loan.created_by?.toString().toLowerCase().includes(searchQueryLower);

      const matchesStatus =
        filterStatus === "All" || loan.status === filterStatus;

      const matchesDate = isWithinDateRange(loan.created_at);

      return matchesSearch && matchesStatus && matchesDate;
    });

  const indexOfLastLoan = currentPage * loansPerPage;
  const indexOfFirstLoan = indexOfLastLoan - loansPerPage;
  const currentLoans = filteredLoans.slice(indexOfFirstLoan, indexOfLastLoan);

  const handleStaffButtonClick = () => navigate("/create-employee");

  const handleModalOpen = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleApplyDateFilter = () => {
    setIsModalOpen(false);
  };

  const handleButtonClick = () => {
    setDropdownVisible(!isDropdownVisible);
  };

  const handleRowClick = (form_id) => {
    setIsViewModalOpen(true);
    setSelectedLoanId(form_id);
  };

  const handleCloseModal = () => {
    setIsViewModalOpen(false);
    setSelectedLoanId(null);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);

    date.setMinutes(date.getMinutes() + 330);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";

    hours = hours % 12;
    hours = hours ? hours : 12;
    const formattedHours = String(hours).padStart(2, "0");

    return `${day}-${month}-${year} ${formattedHours}:${minutes} ${ampm}`;
  };

  const handleStatusChange = (event) => {
    setFilterStatus(event.target.value);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value.toLowerCase());
  };

  const statusClasses = {
    "In Process": "status-inprocess",
    "Open": "status-open",
    "Pending": "status-pending",
    "Approved": "status-approved",
    "Rejected": "status-rejected",
  };

  const handleOptionClick = (option) => {
    switch (option) {
      case "Home Loan":
        navigate("/create-home-loan");
        break;
      case "Personal Loan":
        navigate("/create-personal-loan");
        break;
      case "Doctor Loan":
        navigate("/create-doctor-loan");
        break;
      case "Business Loan":
        navigate("/create-business-loan");
        break;
      case "Car Loan":
        navigate("/create-car-loan");
        break;
      case "Credit Card":
        navigate("/create-credit-card");
        break;
      case "Mortgage Loan":
        navigate("/create-mortgage-loan");
        break;
      case "Insurance":
        navigate("/create-insurance");
        break;
      case "Nri Loan":
        navigate("/create-nri-loan");
        break;
      default:
        break;
    }
    setDropdownVisible(false);
  };

  const handleLoanList = (option) => {
    switch (option) {
      case "Home Loan":
        navigate("/homeloanlist");
        break;
      case "Personal Loan":
        navigate("/personalloanlist");
        break;
      case "Doctor Loan":
        navigate("/doctorloanlist");
        break;
      case "Business Loan":
        navigate("/businessloanlist");
        break;
      case "Car Loan":
        navigate("/carloanlist");
        break;
      case "Credit Card":
        navigate("/creditcardlist");
        break;
      case "Mortgage Loan":
        navigate("/mortgageloanlist");
        break;
      case "Insurance":
        navigate("/insurancelist");
        break;
      case "Nri Loan":
        navigate("/nriloanlist");
        break;
      default:
        break;
    }
    setDropdownVisible(false);
  };

  const handleEdit = (form_id) => {
    if (form_id.startsWith("HL")) {
      navigate(`/edit-home-loan/${form_id}`);
    } else if (form_id.startsWith("PL")) {
      navigate(`/edit-personal-loan/${form_id}`);
    } else if (form_id.startsWith("BL")) {
      navigate(`/edit-business-loan/${form_id}`);
    } else if (form_id.startsWith("CL")) {
      navigate(`/edit-car-loan/${form_id}`);
    } else if (form_id.startsWith("CCL")) {
      navigate(`/edit-creditcard-loan/${form_id}`);
    } else if (form_id.startsWith("IL")) {
      navigate(`/edit-insurance-loan/${form_id}`);
    } else if (form_id.startsWith("DL")) {
      navigate(`/edit-doctor-loan/${form_id}`);
    } else if (form_id.startsWith("NRI")) {
      navigate(`/edit-nri-loan/${form_id}`);
    }
  };

  if (loading) {
    return <Loader />;
  }
  if (error) {
    return <div>{error}</div>;
  }

  return (
    <div className="dashboard-container-content">
      {selectedProduct === "dashboard" && (
        <>
          <h5 className="dashboard-name">
            {adminRole === "admin" ? "Admin Dashboard" : "Employee Dashboard"}
          </h5>
          <div className="loan-dashboard">
            {[
              { label: "Home Loan", key: "homeloan", countKey: "homeLoan" },
              { label: "Personal Loan", key: "personalloan", countKey: "personalLoan" },
              { label: "Business Loan", key: "businessloan", countKey: "businessLoan" },
              // { label: "Car Loan", key: "carloan", countKey: "carLoan" },
              { label: "Mortgage Loan", key: "mortgageloan", countKey: "mortgageLoan" },
              // { label: "Credit Card", key: "creditcard", countKey: "creditCard" },
              // { label: "Insurance", key: "insurance", countKey: "insurance" },
              { label: "Doctor Loan", key: "doctorloan", countKey: "doctorLoan" },
              { label: "Nri Loan", key: "nriloan", countKey: "nriLoan" },
            ].map((loan, index) => (
              <div
                key={index}
                className="loan-button"
                onClick={() => handleLoanList(loan.label)}
              >
                <p className="loan-label">{loan.label}</p>
                <p className="loan-count">{loanCounts[loan.countKey] ?? "00"}</p>
              </div>
            ))}
          </div>
        </>
      )}

      {selectedProduct === "dashboard" && (
        <>
          <h5 className="dashboard-name">All Loans</h5>
          <div className="dropdown-menu-container1">
            <div className="dasboard-search-bar">
              <input
                type="text"
                placeholder="Id, Name, Mobile, AssignedTo"
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <FontAwesomeIcon icon={faSearch} className="search-icon" />
            </div>
            {adminRole === "admin" && (
              <button className="staff-button" onClick={handleStaffButtonClick}>
                <span className="plus-icon">+</span> Add Employee
              </button>
            )}
          </div>
        </>
      )}
       <div className="home-loan-screen">
        <div className="data-wrapper">
          <table className="data-grid">
            <thead>
              <tr>
                <th>Loan ID</th>
                <th>Application Details</th>
                <th>Loan Details</th>
                <th>Status
                  <select
                    className="status-filter"
                    value={filterStatus}
                    onChange={handleStatusChange}
                  >
                    <option value="All">All</option>
                    <option value="Open">Open</option>
                    <option value="In Process">In Process</option>
                    <option value="Pending">Pending</option>
                    <option value="Approved">Approved</option>
                    <option value="Rejected">Rejected</option>
                  </select>
                </th>
                <th>Date
                  <FontAwesomeIcon
                    icon={faCalendarAlt}
                    className="apply-date-icon"
                    onClick={handleModalOpen}
                  />
                  <DateFilterModal
                    isOpen={isModalOpen}
                    onClose={handleModalClose}
                    startDate={startDate}
                    setStartDate={setStartDate}
                    endDate={endDate}
                    setEndDate={setEndDate}
                    applyFilter={handleApplyDateFilter}
                  />
                </th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {currentLoans.length > 0 ? (
                currentLoans.map((loan, index) =>{
                  const company = companyLists.find(
                    (c) => c.company_name === loan.company_name
                  );
                  const bankNames = company
                    ? getBankNames(company.bank_list, loan.company_name)
                    : loan.company_name;

                  return (
                    <tr
                      className="hover-row"
                      key={loan.form_id}
                      onClick={() => handleRowClick(loan.form_id)}
                      style={{ cursor: "pointer" }}
                    >
                      <td>
                        <span className="id-label label-tag label-tag-warning">
                          <span className="table-label">{loan.form_id}</span>
                        </span>
                        <br />
                      </td>
                      <td>
                        <span className="table-label">
                          Name: <span className="table-value">{loan.name}</span>
                        </span>
                        <br />
                        <span className="table-label">
                          Mobile:{" "}
                          <span className="table-value">{loan.mobile}</span>
                        </span>
                        <br />
                        <span className="table-label">
                          Email:{" "}
                          <span className="table-value">{loan.email}</span>
                        </span>
                      </td>
                      <td>
                        {/* {!loan.form_id?.startsWith("BL") &&
                          !loan.form_id?.startsWith("DL") &&
                          loan.company_name &&
                          loan.company_name !== "Others" &&
                          loan.are_you !== "SelfEmployed" &&
                          loan.are_you !== "Doctor" && (
                            <>
                              <span className="table-label">
                                Company Name:{" "}
                                <span className="table-value">
                                  {loan.company_name}
                                </span>
                              </span>
                              <br />
                            </>
                          )} */}
                        {/* {loan.company_name === "Others" && (
                          <>
                            <span className="table-label">
                              Company Name:{" "}
                              <span className="table-value">
                                {loan.others_company}
                              </span>
                            </span>
                            <br />
                          </>
                        )} */}

                        {!loan.form_id?.startsWith("BL") &&
                          !loan.form_id?.startsWith("DL") && (
                            <>
                              <span className="table-label">
                                Company Name:{" "}
                                <span className="table-value">
                                  {loan.others_company}
                                </span>
                              </span>
                              <br />
                            </>
                          )}
                        <span className="table-label">
                          Assigned Bank:{" "}
                          <span className="table-value">{loan.bank_name}</span>
                        </span>
                        {!loan.form_id.startsWith("BL") &&
                          !loan.form_id.startsWith("DL") &&
                          loan.company_name &&
                          loan.are_you !== "SelfEmployed" &&
                          loan.are_you !== "Doctor" && (
                            <>
                              <br />
                              <span className="table-label">
                                Eligible Banks:{" "}
                                <span className="table-value">
                                  {loan.company_name !== "Others"
                                    ? bankNames
                                    : "No Eligible Banks"}
                                </span>
                              </span>
                            </>
                          )}
                      </td>
                      <td>
                        <span className="table-label">
                          Status:{" "}
                          <span
                            className={`table-highlight-value ${
                              statusClasses[loan.status] || ""
                            }`}
                          >
                            {loan.status}
                          </span>
                        </span>
                        <br />
                        <span className="table-label">
                          Assigned To:{" "}
                          <span className="table-value">
                            {loan.assigned_to}
                          </span>
                        </span>
                        <br />
                        <span className="table-label">
                          Created By:{" "}
                          <span className="table-value">{loan.created_by}</span>
                        </span>
                      </td>
                      <td>
                        <span className="table-label">
                          Applied:{" "}
                          <span className="table-value">
                            {formatDate(loan.created_at)}
                          </span>
                        </span>
                        <br />
                      </td>
                      <td>
                        <FaEdit
                          className="edit-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(loan.form_id);
                          }}
                        />
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="8" style={{ textAlign: "center" }}>
                    No loans found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
            <Paginations
            perPageData={loansPerPage}
            data={filteredLoans}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            isShowingPageLength={true}
            paginationDiv="pagination-container"
            paginationClass="pagination"
          />
        </div>
      </div>

      <div className="circle-button-container" ref={dropdownRef}>
        <button className="circular-button" onClick={handleButtonClick}>
          +
        </button>
        {isDropdownVisible && (
          <div className="dropdown-menu">
            <div
              className="dropdown-option"
              onClick={() => handleOptionClick("Home Loan")}
            >
              Home Loan
            </div>
            <div
              className="dropdown-option"
              onClick={() => handleOptionClick("Personal Loan")}
            >
              Personal Loan
            </div>

            <div
              className="dropdown-option"
              onClick={() => handleOptionClick("Business Loan")}
            >
              Business Loan
            </div>
            {/* <div
              className="dropdown-option"
              onClick={() => handleOptionClick("Car Loan")}
            >
              Car Loan
            </div> */}
            <div
              className="dropdown-option"
              onClick={() => handleOptionClick("Mortgage Loan")}
            >
              Mortgage Loan
            </div>
            {/* <div
              className="dropdown-option"
              onClick={() => handleOptionClick("Credit Card")}
            >
              Credit Card
            </div>
            <div
              className="dropdown-option"
              onClick={() => handleOptionClick("Insurance")}
            >
              Insurance
            </div> */}
            <div
              className="dropdown-option"
              onClick={() => handleOptionClick("Doctor Loan")}
            >
              Doctor Loan
            </div>
            <div
              className="dropdown-option"
              onClick={() => handleOptionClick("Nri Loan")}
            >
              NRI Loan
            </div>
          </div>
        )}
      </div>
      {selectedLoanId?.startsWith("HL") && (
        <EditHomeViewModal
          isOpen={isViewModalOpen}
          onClose={handleCloseModal}
          loanId={selectedLoanId}
        />
      )}
      {selectedLoanId?.startsWith("PL") && (
        <EditPersonalViewModal
          isOpen={isViewModalOpen}
          onClose={handleCloseModal}
          loanId={selectedLoanId}
        />
      )}
      {selectedLoanId?.startsWith("BL") && (
        <EditBusinessViewModal
          isOpen={isViewModalOpen}
          onClose={handleCloseModal}
          loanId={selectedLoanId}
        />
      )}
      {selectedLoanId?.startsWith("CL") && (
        <EditCarViewModal
          isOpen={isViewModalOpen}
          onClose={handleCloseModal}
          loanId={selectedLoanId}
        />
      )}
      {selectedLoanId?.startsWith("ML") && (
        <EditMortgageViewModal
          isOpen={isViewModalOpen}
          onClose={handleCloseModal}
          loanId={selectedLoanId}
        />
      )}
      {selectedLoanId?.startsWith("CCL") && (
        <EditCreditViewModal
          isOpen={isViewModalOpen}
          onClose={handleCloseModal}
          loanId={selectedLoanId}
        />
      )}
      {selectedLoanId?.startsWith("IL") && (
        <EditInsuranceViewModal
          isOpen={isViewModalOpen}
          onClose={handleCloseModal}
          loanId={selectedLoanId}
        />
      )}
      {selectedLoanId?.startsWith("DL") && (
        <EditDoctorViewModal
          isOpen={isViewModalOpen}
          onClose={handleCloseModal}
          loanId={selectedLoanId}
        />
      )}

      {selectedLoanId?.startsWith("NRI") && (
        <EditNriViewModal
          isOpen={isViewModalOpen}
          onClose={handleCloseModal}
          loanId={selectedLoanId}
        />
      )}
      <ToastContainer />
    </div>
  );
};

export default DashboardLayout;
